import React, { useState, useRef, useEffect } from 'react';

// Per-language UI strings + the language name the AI should reply in (matches T[lang].aiLang convention elsewhere)
const LANG_CONFIG = {
  'zh-HK': {
    aiLang: 'Traditional Chinese',
    welcome: '你好！我是行程微調助手 🤖\n告訴我你想修改什麼，例如「把第二天下午改成逛街」或「幫我換一間餐廳」',
    quickActions: [
      { label: '🔄 對調兩天', prompt: '請把第一天和第二天的行程對調' },
      { label: '🍽️ 換餐廳風格', prompt: '幫我把今天的餐廳換成另一種風格，推薦一間不同的餐廳' },
      { label: '🛍️ 加購物', prompt: '在行程中加入一段購物時間，例如逛街或市場' },
      { label: '☕ 下午輕鬆一點', prompt: '把今天下午改成輕鬆的活動，例如咖啡館或公園散步' },
      { label: '⚡ 加密行程', prompt: '行程有點鬆散，幫我加多一個景點，讓每天更充實' },
    ],
    placeholder: '輸入修改要求，例如：把第三天下午改成逛夜市…',
    fallbackReply: '抱歉，暫時無法回應，請稍後再試。',
    networkError: '網絡錯誤，請稍後再試 🙏',
    updatedBadge: '✅ 行程已更新',
    fallbackExplanation: '行程已修改！',
    triggerOpen: '收起助手',
    triggerClosed: 'AI 微調助手',
  },
  'zh-CN': {
    aiLang: 'Simplified Chinese',
    welcome: '你好！我是行程微调助手 🤖\n告诉我你想修改什么，例如「把第二天下午改成逛街」或「帮我换一间餐厅」',
    quickActions: [
      { label: '🔄 对调两天', prompt: '请把第一天和第二天的行程对调' },
      { label: '🍽️ 换餐厅风格', prompt: '帮我把今天的餐厅换成另一种风格，推荐一间不同的餐厅' },
      { label: '🛍️ 加购物', prompt: '在行程中加入一段购物时间，例如逛街或市场' },
      { label: '☕ 下午轻松一点', prompt: '把今天下午改成轻松的活动，例如咖啡馆或公园散步' },
      { label: '⚡ 加密行程', prompt: '行程有点松散，帮我加多一个景点，让每天更充实' },
    ],
    placeholder: '输入修改要求，例如：把第三天下午改成逛夜市…',
    fallbackReply: '抱歉，暂时无法回应，请稍后再试。',
    networkError: '网络错误，请稍后再试 🙏',
    updatedBadge: '✅ 行程已更新',
    fallbackExplanation: '行程已修改！',
    triggerOpen: '收起助手',
    triggerClosed: 'AI 微调助手',
  },
  'en': {
    aiLang: 'English',
    welcome: "Hi! I'm your itinerary tweak assistant 🤖\nTell me what you'd like to change, e.g. \"Switch Day 2's afternoon to shopping\" or \"Swap today's restaurant\"",
    quickActions: [
      { label: '🔄 Swap two days', prompt: 'Please swap the itineraries of Day 1 and Day 2' },
      { label: '🍽️ Change restaurant', prompt: "Swap today's restaurant for a different style — recommend a different one" },
      { label: '🛍️ Add shopping', prompt: 'Add a shopping block to the itinerary, e.g. browsing streets or a market' },
      { label: '☕ Relax afternoon', prompt: "Change today's afternoon to something relaxed, like a cafe or a park stroll" },
      { label: '⚡ Add an attraction', prompt: 'The itinerary feels a bit light — add one more attraction to make each day fuller' },
    ],
    placeholder: 'Type your change request, e.g. "Switch Day 3\'s afternoon to a night market…"',
    fallbackReply: "Sorry, I can't respond right now — please try again later.",
    networkError: 'Network error — please try again later 🙏',
    updatedBadge: '✅ Itinerary updated',
    fallbackExplanation: 'Itinerary updated!',
    triggerOpen: 'Hide assistant',
    triggerClosed: 'AI Tweak Assistant',
  },
  'ja': {
    aiLang: 'Japanese',
    welcome: 'こんにちは！旅程調整アシスタントです 🤖\n変更したい内容を教えてください。例：「2日目の午後をショッピングに変更」「今日のレストランを変えてほしい」',
    quickActions: [
      { label: '🔄 2日間を交換', prompt: '1日目と2日目の旅程を入れ替えてください' },
      { label: '🍽️ レストラン変更', prompt: '今日のレストランを別のスタイルに変えて、別のお店を提案してください' },
      { label: '🛍️ 買い物を追加', prompt: '旅程にショッピングの時間（街歩きや市場など）を追加してください' },
      { label: '☕ 午後はゆったり', prompt: '今日の午後をカフェや公園散策などゆったりした活動に変更してください' },
      { label: '⚡ 観光を追加', prompt: '少しゆとりがあるので、観光スポットを1つ追加して各日をより充実させてください' },
    ],
    placeholder: '変更内容を入力（例：3日目の午後をナイトマーケットに変更…）',
    fallbackReply: '申し訳ありません、現在お答えできません。しばらくしてからもう一度お試しください。',
    networkError: 'ネットワークエラーです。しばらくしてからもう一度お試しください 🙏',
    updatedBadge: '✅ 旅程を更新しました',
    fallbackExplanation: '旅程を変更しました！',
    triggerOpen: 'アシスタントを閉じる',
    triggerClosed: 'AI調整アシスタント',
  },
  'ko': {
    aiLang: 'Korean',
    welcome: '안녕하세요! 일정 조정 도우미입니다 🤖\n무엇을 변경하고 싶은지 알려주세요. 예: "2일차 오후를 쇼핑으로 변경" 또는 "오늘 식당을 바꿔줘"',
    quickActions: [
      { label: '🔄 이틀 바꾸기', prompt: '1일차와 2일차 일정을 서로 바꿔주세요' },
      { label: '🍽️ 식당 변경', prompt: '오늘 식당을 다른 스타일로 바꾸고 다른 곳을 추천해주세요' },
      { label: '🛍️ 쇼핑 추가', prompt: '일정에 쇼핑 시간(거리 구경이나 시장 등)을 추가해주세요' },
      { label: '☕ 오후를 여유롭게', prompt: '오늘 오후를 카페나 공원 산책 같은 여유로운 활동으로 바꿔주세요' },
      { label: '⚡ 일정을 알차게', prompt: '일정이 조금 헐거워요. 명소를 하나 더 추가해서 하루를 더 알차게 만들어주세요' },
    ],
    placeholder: '변경 요청을 입력하세요. 예: 3일차 오후를 야시장으로 변경…',
    fallbackReply: '죄송합니다, 지금은 응답할 수 없습니다. 잠시 후 다시 시도해주세요.',
    networkError: '네트워크 오류입니다. 잠시 후 다시 시도해주세요 🙏',
    updatedBadge: '✅ 일정이 업데이트되었습니다',
    fallbackExplanation: '일정이 변경되었습니다!',
    triggerOpen: '도우미 닫기',
    triggerClosed: 'AI 조정 도우미',
  },
};
const getLangCfg = (lang) => LANG_CONFIG[lang] || LANG_CONFIG['zh-HK'];

function Message({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div style={{ display:'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom:12, gap:8, alignItems:'flex-start' }}>
      {!isUser && (
        <div style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg,#f59e0b,#ef4444)',
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>
          🤖
        </div>
      )}
      <div style={{
        maxWidth:'78%', padding:'10px 14px', lineHeight:1.6, fontSize:14,
        borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
        background: isUser ? 'linear-gradient(135deg,#f59e0b,#f97316)' : '#ffffff',
        color: isUser ? '#fff' : '#1e293b',
        boxShadow: isUser ? 'none' : '0 2px 8px rgba(0,0,0,0.08)',
        border: isUser ? 'none' : '1px solid #e2e8f0',
      }}>
        {msg.content}
        {msg.updated && (
          <div style={{ marginTop:8, padding:'5px 10px', background:'rgba(74,222,128,0.15)',
            border:'1px solid rgba(74,222,128,0.4)', borderRadius:8, fontSize:12, fontWeight:700, color:'#16a34a' }}>
            {msg.updatedBadge}
          </div>
        )}
      </div>
    </div>
  );
}

// Inline chat panel (placed directly in the page, not floating)
export function TripRobotPanel({ itinerary, lang, onUpdateItinerary, onClose }) {
  const c = getLangCfg(lang);
  const [msgs, setMsgs] = useState([{ role:'assistant', content: c.welcome }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const prevLang = useRef(lang);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior:'smooth' });
  }, [msgs]);

  // Reset the conversation when the UI language changes — a half-finished chat in the old language would be confusing
  useEffect(() => {
    if (prevLang.current !== lang) {
      prevLang.current = lang;
      setMsgs([{ role:'assistant', content: getLangCfg(lang).welcome }]);
    }
  }, [lang]);

  const send = async (text) => {
    if (!text.trim() || loading) return;
    setMsgs(prev => [...prev, { role:'user', content: text }]);
    setInput('');
    setLoading(true);

    try {
      const systemPrompt = `你是旅遊行程微調助手。用戶有以下行程（JSON）：
${JSON.stringify(itinerary, null, 2)}

根據用戶要求修改行程。回覆格式：
1. 一句說明做了什麼修改
2. 輸出完整修改後的行程JSON，用 \`\`\`json 和 \`\`\` 包住
3. JSON結構必須與原本完全相同（estimatedTotal + days[]）
4. 所有文字內容（包括第1點的說明及JSON內的行程文字）一律使用 ${c.aiLang} 撰寫`;

      const res = await fetch('/api/groq', {
        method:'POST',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({
          model:'openai/gpt-oss-120b',
          max_tokens:4000,
          messages:[
            { role:'system', content:systemPrompt },
            { role:'user', content: text },
          ],
        }),
      });

      const data = await res.json();
      const rawReply = data?.choices?.[0]?.message?.content || c.fallbackReply;

      const jsonMatch = rawReply.match(/```json\s*([\s\S]*?)```/);
      let updated = false;
      if (jsonMatch) {
        try {
          const parsed = JSON.parse(jsonMatch[1]);
          if (parsed?.days?.length) { onUpdateItinerary(parsed); updated = true; }
        } catch { /* ignore */ }
      }

      const explanation = rawReply.replace(/```json[\s\S]*?```/g, '').trim() || c.fallbackExplanation;
      setMsgs(prev => [...prev, { role:'assistant', content:explanation, updated, updatedBadge: c.updatedBadge }]);
    } catch {
      setMsgs(prev => [...prev, { role:'assistant', content: c.networkError }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background:'#f8fafc', borderRadius:'0 0 16px 16px', border:'1.5px solid rgba(255,255,255,0.3)',
      borderTop:'none', overflow:'hidden' }}>

      {/* Messages */}
      <div style={{ padding:'16px 16px 8px', maxHeight:280, overflowY:'auto', background:'#f1f5f9' }}>
        {msgs.map((m,i) => <Message key={i} msg={m}/>)}
        {loading && (
          <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8 }}>
            <div style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg,#f59e0b,#ef4444)',
              display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>🤖</div>
            <div style={{ background:'#fff', borderRadius:'16px 16px 16px 4px', padding:'10px 14px',
              display:'flex', gap:5, alignItems:'center', border:'1px solid #e2e8f0' }}>
              {[0,1,2].map(i => (
                <div key={i} style={{ width:7, height:7, borderRadius:'50%', background:'#94a3b8',
                  animation:'bounce 1s infinite', animationDelay:`${i*0.2}s` }}/>
              ))}
            </div>
          </div>
        )}
        <div ref={bottomRef}/>
      </div>

      {/* Quick actions */}
      <div style={{ padding:'10px 14px 8px', background:'#fff', borderTop:'1px solid #e2e8f0',
        display:'flex', gap:8, overflowX:'auto', scrollbarWidth:'none' }}>
        {c.quickActions.map((a,i) => (
          <button key={i} onClick={() => send(a.prompt)} disabled={loading}
            style={{ flexShrink:0, background:'#f8fafc', border:'1.5px solid #e2e8f0',
              borderRadius:20, padding:'7px 14px', fontSize:13, fontWeight:700,
              color:'#475569', cursor:'pointer', whiteSpace:'nowrap',
              opacity: loading ? 0.5 : 1, transition:'all 0.15s' }}
            onMouseEnter={e => { if(!loading) e.currentTarget.style.borderColor='#f59e0b'; e.currentTarget.style.color='#f59e0b'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor='#e2e8f0'; e.currentTarget.style.color='#475569'; }}>
            {a.label}
          </button>
        ))}
      </div>

      {/* Input */}
      <div style={{ padding:'10px 14px 14px', background:'#fff', display:'flex', gap:10 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key==='Enter' && !e.shiftKey && send(input)}
          placeholder={c.placeholder}
          disabled={loading}
          style={{ flex:1, border:'1.5px solid #e2e8f0', borderRadius:12, padding:'10px 14px',
            fontSize:14, outline:'none', background: loading ? '#f8fafc' : '#fff',
            transition:'border-color 0.15s' }}
          onFocus={e => e.target.style.borderColor='#f59e0b'}
          onBlur={e => e.target.style.borderColor='#e2e8f0'}
        />
        <button onClick={() => send(input)} disabled={loading || !input.trim()}
          style={{ background:'linear-gradient(135deg,#f59e0b,#ef4444)', border:'none',
            borderRadius:12, padding:'10px 16px', color:'#fff', fontWeight:900,
            cursor:'pointer', fontSize:18, opacity:(!input.trim()||loading) ? 0.5 : 1 }}>
          ➤
        </button>
      </div>

      <style>{`
        @keyframes bounce {
          0%,80%,100% { transform:translateY(0); }
          40% { transform:translateY(-5px); }
        }
      `}</style>
    </div>
  );
}

// Trigger button for the orange banner
export function TripRobotTrigger({ open, onToggle, lang }) {
  const c = getLangCfg(lang);
  return (
    <button onClick={onToggle}
      style={{ display:'inline-flex', alignItems:'center', gap:6,
        background: open ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.18)',
        border:'1.5px solid rgba(255,255,255,0.5)', borderRadius:10,
        padding:'7px 13px', color:'white', fontSize:12, fontWeight:900,
        cursor:'pointer', transition:'all 0.15s', backdropFilter:'blur(4px)' }}>
      🤖 {open ? c.triggerOpen : c.triggerClosed}
    </button>
  );
}
